// Authentication service for the family tree application

import { User } from '../models/types';

// Mock users for simple authentication
const users: User[] = [
  {
    username: 'admin',
    password: 'Family@2024',
    isAdmin: true
  },
  {
    username: 'member',
    password: 'Member@2024',
    isAdmin: false
  }
];

// Helper to check if localStorage is available
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.error('localStorage is not available:', e);
    return false;
  }
};

export const AuthService = {
  login: (username: string, password: string): Promise<User | null> => {
    console.log(`AuthService: Attempting login for user: ${username}`);
    
    return new Promise((resolve) => {
      // Simulate API call delay
      setTimeout(() => {
        const user = users.find(
          (u) => u.username === username && u.password === password
        );
        
        if (user) {
          console.log(`AuthService: User ${username} found, logging in`);
          
          // Don't return password in response
          const { password, ...userWithoutPassword } = user;
          
          // Check localStorage availability before setting
          if (isLocalStorageAvailable()) {
            try {
              console.log('AuthService: Storing user in localStorage');
              localStorage.setItem('currentUser', JSON.stringify(userWithoutPassword));
              console.log('AuthService: User data stored successfully');
            } catch (e) {
              console.error('AuthService: Error storing user in localStorage:', e);
            }
          }
          
          resolve(userWithoutPassword as User);
        } else {
          console.log(`AuthService: Login failed for ${username} - invalid credentials`);
          resolve(null);
        }
      }, 500);
    });
  },
  
  logout: (): void => {
    console.log('AuthService: Logging out user');
    
    if (isLocalStorageAvailable()) {
      try {
        localStorage.removeItem('currentUser');
        console.log('AuthService: User removed from localStorage');
      } catch (e) {
        console.error('AuthService: Error removing user from localStorage:', e);
      }
    }
  },
  
  getCurrentUser: (): User | null => {
    console.log('AuthService: Getting current user from localStorage');
    
    if (!isLocalStorageAvailable()) {
      console.error('AuthService: localStorage not available for getCurrentUser');
      return null;
    }
    
    try {
      const userStr = localStorage.getItem('currentUser');
      console.log('AuthService: Retrieved user string:', userStr ? 'found' : 'not found');
      
      if (userStr) {
        const user = JSON.parse(userStr);
        console.log('AuthService: Parsed user object:', user);
        return user;
      }
      return null;
    } catch (e) {
      console.error('AuthService: Error getting current user:', e);
      return null;
    }
  },
  
  isAuthenticated: (): boolean => {
    if (!isLocalStorageAvailable()) {
      return false;
    }
    return localStorage.getItem('currentUser') !== null;
  },
  
  isAdmin: (): boolean => {
    const user = AuthService.getCurrentUser();
    return user ? user.isAdmin : false;
  }
};
